import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { RouteLocationRaw } from 'vue-router'
import router from '../router/router.js'

interface PendingNavigation {
  to: RouteLocationRaw
}

export const useUnsavedChangesStore = defineStore('unsavedChanges', () => {
  const hasUnsavedChanges = ref(false)
  const pendingNavigation = ref<PendingNavigation | null>(null)
  const pendingCallback = ref<(() => void) | null>(null)
  const isLoading = ref(false)
  const saveCallback = ref<(() => Promise<void>) | null>(null)
  const discardCallback = ref<(() => Promise<void>) | null>(null)

  function setUnsavedChanges(value: boolean) {
    hasUnsavedChanges.value = value
  }

  function setPendingNavigation(navigation: PendingNavigation | null) {
    pendingNavigation.value = navigation
  }

  function setPendingCallback(callback: (() => void) | null) {
    pendingCallback.value = callback
  }

  function clearPending() {
    pendingCallback.value = null
    pendingNavigation.value = null
  }

  function setSaveCallback(callback: (() => Promise<void>) | null) {
    saveCallback.value = callback
  }

  function setDiscardCallback(callback: (() => Promise<void>) | null) {
    discardCallback.value = callback
  }

  async function handleSave() {
    isLoading.value = true
    try {
      if (saveCallback.value) {
        await saveCallback.value()
      }
      setUnsavedChanges(false)
      continueNavigation()
    } catch (error) {
      console.error('Save failed:', error)
    } finally {
      isLoading.value = false
    }
  }

  async function handleDiscard() {
    isLoading.value = true
    try {
      if (discardCallback.value) {
        await discardCallback.value()
      }
      setUnsavedChanges(false)
      continueNavigation()
    } catch (error) {
      console.error('Discard failed:', error)
    } finally {
      isLoading.value = false
    }
  }

  function handleCancel() {
    clearPending()
  }

  function continueNavigation() {
    const navigation = pendingNavigation.value
    const callback = pendingCallback.value
    clearPending()

    if (navigation) {
      router.push(navigation.to)
    }

    if (callback) {
      callback()
    }
  }

  return {
    hasUnsavedChanges,
    pendingNavigation,
    pendingCallback,
    isLoading,
    saveCallback,
    discardCallback,
    setUnsavedChanges,
    setPendingNavigation,
    setPendingCallback,
    clearPending,
    setSaveCallback,
    setDiscardCallback,
    handleSave,
    handleDiscard,
    handleCancel,
  }
})
